import { useEffect } from 'react';
import { CheckCircle2, Clock, Zap, Target, AlertCircle, ChevronRight, Sparkles, Users } from 'lucide-react';
import trackEvent from '../../services/trackEvent';
import ScoreCelebration from './ScoreCelebration';

/**
 * Verdict de maîtrise — écran de fin "wahoo" : anneau animé (ScoreCelebration)
 * + niveau + diagnostic + prochaine action. Tout est calculé côté client à
 * partir de la session courante, donc opérationnel dès le tout premier quiz
 * (aucun historique requis).
 *
 *  - Transverse (plusieurs sous-sujets) → "Points à consolider" par sous-sujet.
 *  - Sujet unique → barre réussites / erreurs + temps + rythme.
 *
 * Props : pct, score, total, color, timeInSeconds, avgTime, avgTimeOthers, isTransverse,
 *         bySousSujet[{label,total,correct,pct}], nbErrors, modeLabel,
 *         contextLabel, onReviewAll(), onReviewErrors()|null,
 *         primaryAction|null, upsell{title,subtitle,cta,onClick}|null
 */

const LEVELS = [
    { min: 90, label: 'Maîtrise',       diag: 'Niveau remarquable. Espace tes révisions pour ancrer durablement ces acquis.' },
    { min: 70, label: 'Solide',         diag: 'Tu maîtrises l’essentiel — il reste quelques notions à stabiliser.' },
    { min: 50, label: 'En progression', diag: 'Les bases sont là, mais certaines notions ne sont pas encore assez sûres.' },
    { min: 0,  label: 'À consolider',   diag: 'Ce sujet mérite une reprise méthodique. Ce n’est pas un échec : c’est un diagnostic.' },
];

function levelFor(pct) {
    return LEVELS.find(l => pct >= l.min) || LEVELS[LEVELS.length - 1];
}

function fmtTime(s) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
}

// Rythme factuel (jamais jugeant) déduit du temps moyen par question ; comparé aux autres utilisateurs
// sur les mêmes questions quand on a leur temps médian (api/qcm/pace.php), sinon repères fixes.
function rythmeFor(avg, others) {
    if (avg == null) return null;
    if (others) {
        const ref = `${Math.round(others)}s pour les autres`;
        if (avg < others * 0.8) return { label: `Plus rapide · ${ref}`, icon: Users };
        if (avg > others * 1.25) return { label: `Plus posé · ${ref}`, icon: Users };
        return { label: `Même rythme · ${ref}`, icon: Users };
    }
    if (avg < 8) return { label: 'Rythme rapide', icon: Zap };
    if (avg <= 25) return { label: 'Rythme régulier', icon: Clock };
    return { label: 'Rythme posé', icon: Clock };
}

function barColor(pct, accent) {
    if (pct >= 75) return accent;
    if (pct >= 50) return '#f59e0b';
    return '#ef4444';
}

export default function MasteryVerdict({
    pct = 0, score = 0, total = 0, color = '#7c3aed',
    timeInSeconds = 0, avgTime = null, avgTimeOthers = null,
    isTransverse = false, bySousSujet = [],
    nbErrors = 0, modeLabel = 'Classique', contextLabel = '',
    onReviewAll, onReviewErrors, primaryAction, upsell = null,
}) {
    // Mesure : proposition d'abonnement AFFICHÉE en fin de quiz (avant, seule l'ouverture
    // de l'offre était comptée). Source fin_quiz | fin_quiz_guide ; doublons écartés côté serveur.
    useEffect(() => {
        if (upsell) trackEvent('upsell_shown', { source: upsell.source || 'fin_quiz', scopeId: upsell.scopeId });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const level = levelFor(pct);
    const accent = pct >= 70 ? color : pct >= 50 ? '#f59e0b' : '#ef4444';
    const rythme = rythmeFor(avgTime, avgTimeOthers);

    // Tri faible→fort pour la priorité de révision (transverse).
    const sorted = [...bySousSujet].sort((a, b) => a.pct - b.pct);
    const shown = sorted.slice(0, 6);
    const weak = sorted.filter(s => s.pct < 70).slice(0, 2);

    return (
        <div style={{
            position: 'relative', background: 'var(--card-bg, #fff)', borderRadius: 20,
            border: '1px solid var(--card-border)', boxShadow: '0 18px 60px rgba(0,0,0,0.32)',
            overflow: 'hidden', textAlign: 'center',
        }}>
            <style>{`
                .mv-pop { animation: mvPop 0.45s ease both; }
                @keyframes mvPop { 0% { opacity:0; } 100% { opacity:1; } }
                @media (prefers-reduced-motion: reduce) { .mv-pop { animation: none; } }
                /* Mobile : le score n'est dit qu'une fois (l'anneau) ; « Revoir mes N erreurs » porte le reste. */
                @media (max-width: 640px) { .mv-chip-score, .mv-subject-bar { display: none !important; } }
            `}</style>

            {/* Liseré supérieur dans la couleur du niveau */}
            <div style={{ height: 5, background: `linear-gradient(90deg, ${accent}, ${accent}99)` }} />

            <div style={{ padding: '20px 22px 22px' }}>
                {/* Overline */}
                <div style={{ fontSize: '0.7em', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>
                    Verdict de maîtrise
                </div>

                <ScoreCelebration pct={pct} correct={score} total={total} color={color}
                    size={typeof window !== 'undefined' && window.matchMedia?.('(max-width: 640px)').matches ? 116 : 150} />

                {/* Niveau + diagnostic */}
                <div className="mv-pop" style={{ marginTop: 12 }}>
                    <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: 999, fontSize: '0.82em', fontWeight: 700, color: '#fff', background: accent }}>
                        Niveau : {level.label}
                    </span>
                    <p style={{ fontSize: '0.86em', color: 'var(--text-muted)', lineHeight: 1.5, margin: '10px auto 0', maxWidth: 360 }}>
                        {level.diag}
                    </p>
                </div>

                {/* Bandeau de stats */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', margin: '16px 0' }}>
                    <Chip icon={CheckCircle2} color={accent} className="mv-chip-score">{score}/{total} correct</Chip>
                    {avgTime != null
                        ? <Chip icon={Clock}>{avgTime}s / question</Chip>
                        : <Chip icon={Clock}>{fmtTime(timeInSeconds)}</Chip>}
                    {rythme && <Chip icon={rythme.icon}>{rythme.label}</Chip>}
                </div>

                {/* ---- Diagnostic ---- */}
                {isTransverse ? (
                    <div style={{ textAlign: 'left', marginTop: 4 }}>
                        <SectionTitle icon={Target} color={accent}>Points à consolider</SectionTitle>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                            {shown.map((s, i) => {
                                const c = barColor(s.pct, color);
                                return (
                                    <div key={i}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em', marginBottom: 3 }}>
                                            <span style={{ color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '78%' }}>{s.label}</span>
                                            <span style={{ color: c, fontWeight: 700 }}>{s.pct}%</span>
                                        </div>
                                        <div style={{ height: 7, borderRadius: 6, background: 'var(--border, #e5e7eb)', overflow: 'hidden' }}>
                                            <div style={{ height: '100%', borderRadius: 6, width: `${s.pct}%`, background: c }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {weak.length > 0 && (
                            <p style={{ fontSize: '0.8em', color: 'var(--text-muted)', marginTop: 10 }}>
                                <Sparkles size={13} style={{ verticalAlign: '-2px', color: accent, marginRight: 4 }} />
                                Priorité de révision : <strong style={{ color: 'var(--text)' }}>{weak.map(w => w.label).join(', ')}</strong>
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="mv-subject-bar" style={{ textAlign: 'left', marginTop: 4 }}>
                        <SectionTitle icon={Target} color={accent}>Sur ce sujet</SectionTitle>
                        {/* Barre segmentée réussites / erreurs */}
                        <div style={{ display: 'flex', height: 9, borderRadius: 6, overflow: 'hidden', marginTop: 8, background: 'var(--border, #e5e7eb)' }}>
                            <div style={{ width: `${pct}%`, background: accent }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em', marginTop: 6, color: 'var(--text-muted)' }}>
                            <span><CheckCircle2 size={13} style={{ verticalAlign: '-2px', color: accent }} /> {score} réussite{score > 1 ? 's' : ''}</span>
                            {nbErrors > 0
                                ? <span><AlertCircle size={13} style={{ verticalAlign: '-2px', color: '#ef4444' }} /> {nbErrors} à revoir</span>
                                : <span style={{ color: accent, fontWeight: 600 }}>Sans-faute 🎯</span>}
                        </div>
                    </div>
                )}

                {/* ---- Débloquer la suite (compte gratuit uniquement) ----
                    Placé AVANT « Prochaine action » : quelqu'un qui vient de
                    réussir ses questions d'essai est au meilleur moment pour
                    savoir ce qu'il y a derrière — et à quel prix. On annonce le
                    volume réel et le tarif, jamais une promesse vague. */}
                {upsell && (
                    <div style={{
                        marginTop: 18, padding: '14px 16px', borderRadius: 12,
                        border: `1px solid ${accent}40`, background: `${accent}12`, textAlign: 'left',
                    }}>
                        <div style={{ fontSize: '0.88em', fontWeight: 700, color: 'var(--heading, var(--text))' }}>
                            {upsell.title}
                        </div>
                        {upsell.subtitle && (
                            <div style={{ fontSize: '0.8em', color: 'var(--text-muted)', marginTop: 3 }}>
                                {upsell.subtitle}
                            </div>
                        )}
                        <button
                            className="btn btn-primary btn-sm"
                            style={{ marginTop: 10, width: '100%' }}
                            onClick={upsell.onClick}
                        >
                            {upsell.cta} <ChevronRight size={15} />
                        </button>
                    </div>
                )}

                {/* ---- Prochaine action ---- */}
                <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: '0.7em', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
                        Prochaine action
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {primaryAction && (
                            <button className="btn btn-primary" onClick={primaryAction.onClick}>
                                {primaryAction.label} <ChevronRight size={15} />
                            </button>
                        )}
                        {onReviewErrors && (
                            <button className={primaryAction ? 'btn btn-ghost btn-sm' : 'btn btn-primary'} onClick={onReviewErrors}>
                                {nbErrors > 1 ? `Revoir mes ${nbErrors} erreurs` : 'Revoir mon erreur'}
                            </button>
                        )}
                        <button className={(primaryAction || onReviewErrors) ? 'btn btn-ghost btn-sm' : 'btn btn-primary'} onClick={onReviewAll}>
                            {(primaryAction || onReviewErrors) ? 'Toutes mes réponses' : <>Revoir mes réponses <ChevronRight size={15} /></>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Chip({ icon: Icon, color, className, children }) {
    return (
        <span className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.78em', fontWeight: 600, color: 'var(--text)', background: 'var(--bg-subtle, rgba(0,0,0,0.04))', border: '1px solid var(--card-border)', borderRadius: 999, padding: '4px 10px' }}>
            <Icon size={13} style={{ color: color || 'var(--text-muted)' }} /> {children}
        </span>
    );
}

function SectionTitle({ icon: Icon, color, children }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82em', fontWeight: 700, color: 'var(--text)' }}>
            <Icon size={15} style={{ color }} /> {children}
        </div>
    );
}
